import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, Phone } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO_URL = '/images/logo-done.png';

export function Navbar() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  const navItems = [
    { path: '/', label: getText('Accueil', 'الرئيسية', 'Home') },
    { path: '/groupe', label: getText('Groupe', 'المجموعة', 'Group') },
    { path: '/accessoires', label: getText('Accessoires', 'الإكسسوارات', 'Accessories') },
    { path: '/telecom', label: getText('Télécom', 'الاتصالات', 'Telecom') },
    { path: '/bureau-etude', label: getText('Retail Tech', 'تقنية التجزئة', 'Retail Tech') },
    { path: '/ecosysteme', label: getText('Écosystème', 'النظام البيئي', 'Ecosystem') },
  ];
  
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);
  
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  
  return (
    <>
      {/* Main Navbar - SPACIAZ STYLE with rounded bottom */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 px-4 pt-4"
        data-testid="navbar"
      >
        <div 
          className={`mx-auto max-w-[1400px] transition-all duration-500 ${
            isScrolled 
              ? 'bg-white shadow-[0_4px_30px_rgba(0,0,0,0.1)]' 
              : 'bg-white'
          }`}
          style={{ borderRadius: '0 0 30px 30px' }}
        >
          <div className="px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="relative z-10" data-testid="logo-link">
                <img 
                  src={LOGO_URL} 
                  alt="D.One" 
                  className="h-8 w-auto"
                />
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-8">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative text-sm font-medium transition-colors duration-300 ${
                      isActive(item.path) 
                        ? 'text-[#f4b00f]' 
                        : 'text-gray-600 hover:text-[#f4b00f]'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  >
                    {item.label}
                    {isActive(item.path) && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#f4b00f]"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                ))}
              </div>
              
              {/* Right side */}
              <div className="flex items-center gap-4">
                <LanguageSwitcher />
                
                <Link 
                  to="/contact" 
                  className="hidden md:flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#ffcc4d] via-[#f4b00f] to-[#d99a00] text-black rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-[#f4b00f]/30 transition-all duration-300"
                  data-testid="nav-cta"
                >
                  {getText('Nous contacter', 'اتصل بنا', 'Get In Touch')}
                </Link>
                
                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden w-12 h-12 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 transition-all duration-300"
                  data-testid="mobile-menu-btn"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>
      
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-main pt-32 pb-10 h-full flex flex-col">
              <div className="flex-1 flex flex-col justify-center gap-1">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <Link
                      to={item.path}
                      className={`block py-4 text-3xl font-semibold transition-colors ${
                        isActive(item.path) ? 'text-[#f4b00f]' : 'text-gray-800 hover:text-[#f4b00f]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Link to="/contact" className="btn-primary w-full justify-center">
                  {getText('Nous contacter', 'اتصل بنا', 'Contact us')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
